import { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { WrenchIcon as Wrench, CurrencyEurIcon as CurrencyEur, FloppyDiskIcon as FloppyDisk, CopyIcon as Copy } from "@phosphor-icons/react";
import { ReportTargetSection } from "@/components/ReportTargetSection";
import { SistemistiSettings } from "@/components/SistemistiSettings";
import { ToolboxIcon as Toolbox } from "@phosphor-icons/react";

function ReportOreTargetSection() {
  const nowY = new Date().getFullYear();
  const [anno, setAnno] = useState(nowY);
  const [giorn, setGiorn] = useState("");
  const [oraria, setOraria] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async (a) => {
    try {
      const { data } = await api.get(`/impostazioni/reportistica/assistenza-tecnica/report-ore-target/${a}`);
      setGiorn(data.target_media_fatturato_giornaliera ?? "");
      setOraria(data.target_media_fatturato_oraria ?? "");
      setNote(data.note || "");
    } catch { setGiorn(""); setOraria(""); setNote(""); }
  };
  useEffect(() => { load(anno); }, [anno]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/impostazioni/reportistica/assistenza-tecnica/report-ore-target/${anno}`, {
        target_media_fatturato_giornaliera: giorn === "" ? null : parseFloat(giorn),
        target_media_fatturato_oraria: oraria === "" ? null : parseFloat(oraria),
        note,
      });
      toast.success(`Target Report Ore ${anno} salvato`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };
  const copyPrev = async () => {
    try {
      const { data } = await api.get(`/impostazioni/reportistica/assistenza-tecnica/report-ore-target/${anno - 1}`);
      setGiorn(data.target_media_fatturato_giornaliera ?? "");
      setOraria(data.target_media_fatturato_oraria ?? "");
      toast.info(`Valori copiati da ${anno - 1}`);
    } catch { toast.error(`Nessun target per ${anno - 1}`); }
  };

  return (
    <div className="space-y-5" data-testid="report-ore-target-section">
      <p className="text-sm text-muted-foreground">Target delle medie di fatturato usati nella dashboard <b>Report Ore</b> per confronto con i valori prodotti.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Anno</Label>
          <Input type="number" value={anno} onChange={(e) => setAnno(parseInt(e.target.value, 10) || nowY)} className="mt-1 rounded-sm h-9" data-testid="rot-anno" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Target media fatturato giornaliera (€)</Label>
          <Input type="number" step="0.01" value={giorn} onChange={(e) => setGiorn(e.target.value)} className="mt-1 rounded-sm h-9 tabular-nums" placeholder="es. 2230" data-testid="rot-giornaliera" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Target media fatturato oraria (€)</Label>
          <Input type="number" step="0.01" value={oraria} onChange={(e) => setOraria(e.target.value)} className="mt-1 rounded-sm h-9 tabular-nums" placeholder="es. 65" data-testid="rot-oraria" />
        </div>
      </div>
      <div className="max-w-3xl">
        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Note</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 rounded-sm h-9" placeholder="—" data-testid="rot-note" />
      </div>
      <div className="flex items-center gap-2">
        <Button className="rounded-sm gap-1.5" onClick={save} disabled={saving} data-testid="rot-save"><FloppyDisk size={16} /> {saving ? "Salvataggio…" : "Salva"}</Button>
        <Button variant="outline" className="rounded-sm gap-1.5" onClick={copyPrev} data-testid="rot-copy-prev"><Copy size={15} /> Copia da anno precedente</Button>
      </div>
    </div>
  );
}

export function ReportisticaSettings() {
  const [area, setArea] = useState("assistenza");
  const [tab, setTab] = useState("budget-target");
  const TABS = area === "assistenza" ? [
    { k: "budget-target", l: "Budget/Target" },
    { k: "report-ore", l: "Report Ore" },
  ] : [
    { k: "configurazione", l: "Configurazione" },
  ];
  const selectArea = (a) => { setArea(a); setTab(a === "assistenza" ? "budget-target" : "configurazione"); };
  return (
    <div className="flex flex-col md:flex-row gap-5" data-testid="reportistica-settings">
      {/* Sotto-menu verticale (aree reportistica) */}
      <aside className="md:w-52 flex-shrink-0 space-y-1">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold px-2 mb-2">Aree</div>
        <button onClick={() => selectArea("assistenza")} className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium ${area === "assistenza" ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-secondary/50"}`} data-testid="submenu-assistenza-tecnica">
          <Wrench size={16} weight="fill" /> Assistenza Tecnica
        </button>
        <button onClick={() => selectArea("sistemisti")} className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium ${area === "sistemisti" ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-secondary/50"}`} data-testid="submenu-sistemisti">
          <Toolbox size={16} weight="fill" /> Sistemisti
        </button>
      </aside>
      <div className="flex-1 min-w-0">
        {/* Menu orizzontale schede — SOLO area Assistenza (Sistemisti ha il proprio menu Configurazione|Premi) */}
        {area === "assistenza" && (
          <div className="flex items-center gap-1 border-b border-border mb-5">
            {TABS.map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                data-testid={`settings-at-tab-${t.k}`}>{t.l}</button>
            ))}
          </div>
        )}
        {area === "sistemisti" && <SistemistiSettings />}
        {area === "assistenza" && tab === "budget-target" && <ReportTargetSection />}
        {area === "assistenza" && tab === "report-ore" && (
          <Card className="rounded-sm border-border shadow-none p-6" data-testid="report-ore-settings-card">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><CurrencyEur size={20} weight="bold" className="text-primary" /></div>
              <div>
                <h3 className="font-heading font-semibold">Report Ore</h3>
                <p className="text-xs text-muted-foreground">Target medie di fatturato (giornaliera e oraria) per anno.</p>
              </div>
            </div>
            <ReportOreTargetSection />
          </Card>
        )}
      </div>
    </div>
  );
}
